import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle } from "lucide-react";

export default function Legal() {
  const { slug } = useParams();
  const [policies, setPolicies] = useState([]);
  const [doc, setDoc] = useState(null);

  useEffect(() => { api.get("/policies").then((r) => setPolicies(r.data)); }, []);
  useEffect(() => {
    setDoc(null);
    if (slug) api.get(`/policies/${slug}`).then((r) => setDoc(r.data)).catch(() => setDoc(false));
  }, [slug]);

  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="legal-page">
      <p className="overline">Legal, privacy and trust</p>
      <h1 className="mt-3 text-3xl sm:text-4xl font-semibold">
        {doc ? doc.title : "Policies and disclaimers"}
      </h1>

      {!slug && (
        <>
          <p className="mt-4 max-w-3xl text-sm text-slate-600">
            Every policy is versioned. Publishing a new version never overwrites an earlier one, and any
            acceptance already recorded stays linked to the version that applied at the time.
          </p>
          {policies.some((p) => p.review_status !== "approved") && (
            <p className="card p-4 mt-6 flex gap-3 text-sm bg-amber-50 border-amber-200" data-testid="policy-review-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
              <span>
                <strong>Professional review outstanding.</strong> {policies.filter((p) => p.review_status !== "approved").length} of{" "}
                {policies.length} policies below are still marked for solicitor or internal review. They are
                published here as working drafts and must not be relied upon in production until EuropEdu's
                adviser has approved them and the approval has been recorded.
              </span>
            </p>
          )}
          <ul className="mt-8 grid gap-3 md:grid-cols-2">
            {policies.map((p) => (
              <li key={p.slug}>
                <Link to={`/legal/${p.slug}`} data-testid={`policy-link-${p.slug}`} className="card p-5 block">
                  <p className="font-semibold">{p.title}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Version {p.version} · effective {new Date(p.effective_date).toLocaleDateString("en-GB")}
                  </p>
                  <span className={`pill mt-3 ${p.review_status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
                    {p.review_status.replace(/_/g, " ")}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {doc === false && <p className="mt-6 text-sm text-slate-600">That policy could not be found. <Link className="underline" to="/legal">View all policies</Link>.</p>}

      {doc && (
        <article className="mt-6" data-testid="policy-document">
          <p className="text-xs text-slate-500">
            Version {doc.version} · jurisdiction {doc.jurisdiction} · effective{" "}
            {new Date(doc.effective_date).toLocaleDateString("en-GB")}
            {doc.legal_reviewer ? ` · reviewed by ${doc.legal_reviewer}` : ""}
          </p>
          {doc.review_status !== "approved" && (
            <p className="card p-4 mt-4 flex gap-3 text-sm bg-amber-50 border-amber-200" data-testid="policy-review-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
              <span>
                <strong>{doc.review_status.replace(/_/g, " ")}.</strong> This wording has not yet been
                approved by EuropEdu's professional adviser and must not be relied upon in production.
              </span>
            </p>
          )}
          <div className="mt-6 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{doc.body}</div>
          <Link to="/legal" className="btn btn-ghost mt-8">All policies</Link>
        </article>
      )}
    </div>
  );
}
